import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from '@material-ui/core'
import PropTypes from 'prop-types'
import React from 'react'

import {
  fetchAtHomeRescheduleOptions,
  rescheduleAtHomeSession
} from 'api/atHome'
import constants from 'app/constants'
import { eventAlert } from 'app/helpers'

import { fmtDate, fmtTime, todayLocal } from './format'

/**
 * Move ONE module session: same teacher, another time (prototype rule). The
 * server offers the teacher's listed times over the next three weeks that do
 * not clash; ops may also type any date and time. The parent's own app holds
 * the 24-hour cutoff; Alfred may move a session closer than that. The session
 * keeps its number ("Session 3 of 8") and payment; its start code changes.
 */
const MoveSessionDialog = ({ booking, open, onClose, onMoved }) => {
  const [options, setOptions] = React.useState(null)
  const [pick, setPick] = React.useState('')
  const [date, setDate] = React.useState('')
  const [time, setTime] = React.useState('')
  const [reason, setReason] = React.useState('')
  const [busy, setBusy] = React.useState(false)

  React.useEffect(() => {
    if (!open || !booking) return
    setPick('')
    setDate('')
    setTime('')
    setReason('')
    setOptions(null)
    fetchAtHomeRescheduleOptions(booking.uuid)
      .then(setOptions)
      .catch(() => setOptions([]))
  }, [open, booking])

  const custom = pick === 'other'
  const chosen = custom
    ? date && time
      ? { date, start_time: time }
      : null
    : pick
    ? { date: pick.split('T')[0], start_time: pick.split('T')[1] }
    : null

  const submit = async () => {
    if (!chosen) return
    setBusy(true)
    try {
      await rescheduleAtHomeSession(booking.uuid, { ...chosen, reason })
      eventAlert('Session moved', constants.SUCCESS)
      onMoved()
      onClose()
    } catch (error) {
      // request.js already surfaces a toast
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={() => !busy && onClose()}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Move this session</DialogTitle>
      <DialogContent>
        {booking && (
          <DialogContentText>
            {booking.childName}&apos;s {booking.sessionLabel || 'session'} is on{' '}
            {booking.dateLabel}, {booking.timeLabel} with {booking.teacherName}.
            It stays with {booking.teacherName}; pick another time. The parent
            gets a new start code for it.
          </DialogContentText>
        )}
        {options === null && (
          <DialogContentText>
            Loading the teacher&apos;s times…
          </DialogContentText>
        )}
        {options && (
          <RadioGroup value={pick} onChange={(e) => setPick(e.target.value)}>
            {options.map((o) => {
              const v = `${o.date}T${o.start_time}`
              return (
                <FormControlLabel
                  key={v}
                  value={v}
                  control={<Radio color="primary" size="small" />}
                  label={`${fmtDate(o.date)}, ${fmtTime(o.start_time)}`}
                />
              )
            })}
            <FormControlLabel
              value="other"
              control={<Radio color="primary" size="small" />}
              label={
                options.length
                  ? 'Another date and time'
                  : 'No listed time is free in the next three weeks; pick a date and time'
              }
            />
          </RadioGroup>
        )}
        {custom && (
          <div style={{ display: 'flex', gap: 12 }}>
            <TextField
              type="date"
              label="Date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              margin="dense"
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: todayLocal() }}
            />
            <TextField
              type="time"
              label="Start time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              margin="dense"
              InputLabelProps={{ shrink: true }}
            />
          </div>
        )}
        <TextField
          label="Reason (what the parent said)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          fullWidth
          multiline
          minRows={2}
          margin="normal"
        />
        <Typography variant="caption" color="textSecondary">
          Parents can move a session themselves up to 24 hours before it starts;
          inside that, only Alfred can.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Keep as is
        </Button>
        <Button
          onClick={submit}
          color="primary"
          variant="contained"
          disabled={!chosen || busy}
        >
          {busy ? 'Moving…' : 'Move session'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

MoveSessionDialog.propTypes = {
  booking: PropTypes.object,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onMoved: PropTypes.func.isRequired
}

export default MoveSessionDialog
